import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { canHandoffToNativeScheme } from '../../utils/inAppBrowser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

const isInsideCustomTab = () => {
  try {
    return localStorage.getItem('allure_native_checkout_pending') === '1';
  } catch {
    return false;
  }
};

export default function GuestPaymentMethodCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: 'processing', message: 'Confirming your card…' });
  const ranRef = useRef(false);
  const [inCustomTab] = useState(isInsideCustomTab);

  // Auto-handoff to the native app via the `allurexp://` custom URI
  // scheme so Chrome Custom Tab closes itself as part of the Android
  // intent switch — no passkey prompt, no manual ✕ tap. See the twin
  // comment in `/app/frontend/src/pages/PaymentCompletePage.js` for
  // the full rationale.
  //
  // GUARD: Skip inside third-party in-app WebViews (Instagram, FB,
  // TikTok, Snapchat, WeChat, LinkedIn, Twitter, WhatsApp, Discord).
  // These browsers swallow custom URI schemes and would strand the
  // user on a blank page after Apple Pay completes.
  const deepLinkFiredRef = useRef(false);
  if (!deepLinkFiredRef.current) {
    deepLinkFiredRef.current = true;
    try {
      const rawQs = window.location.search || '';
      const hasVivaParams = params.get('t') || params.get('s') || params.get('transactionId') || params.get('orderCode') || params.has('cancel') || params.get('failed') === '1' || params.get('eventId') === '-1';
      if (hasVivaParams && canHandoffToNativeScheme()) {
        const target = `allurexp://payment-complete${rawQs}`;
        window.location.replace(target);
      }
    } catch (_e) { /* ignore */ }
  }

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    try { localStorage.removeItem('allure_native_checkout_pending'); } catch (_e) { /* noop */ }

    const transactionId = params.get('t') || params.get('transactionId') || '';
    const eventId = params.get('eventId') || '';
    const failed = params.get('failed') === '1';
    const orderCode = params.get('s') || params.get('orderCode') || '';

    const safeNavigate = (path, delay) => {
      if (inCustomTab) return;
      setTimeout(() => navigate(path, { replace: true }), delay);
    };

    if (failed || (!transactionId && eventId === '-1')) {
      setState({
        status: inCustomTab ? 'success' : 'error',
        message: inCustomTab ? 'Tap \u2715 above to return to Allure.' : 'Card setup was cancelled or failed.',
      });
      safeNavigate('/guest/settings/payment-method', 2200);
      return;
    }

    if (!transactionId) {
      setState({
        status: inCustomTab ? 'success' : 'error',
        message: inCustomTab ? 'Tap \u2715 above to return to Allure.' : 'Missing transaction reference.',
      });
      safeNavigate('/guest/settings/payment-method', 2200);
      return;
    }

    (async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/guest/payment-method/finalize`,
          { transaction_id: transactionId, order_code: orderCode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setState({
          status: 'success',
          message: inCustomTab ? 'Card saved! Tap \u2715 above to return to Allure.' : 'Card saved! You can now pay with one tap.',
        });
        safeNavigate('/guest/settings/payment-method', 1600);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Could not save your card. Please try again.';
        setState({
          status: 'error',
          message: inCustomTab ? 'Close this window and try again in the app.' : detail,
        });
        safeNavigate('/guest/settings/payment-method', 2400);
      }
    })();
  }, [params, navigate, inCustomTab]);

  const colorMap = {
    processing: '#703FF6',
    success: '#1F8E2A',
    error: '#C92016',
  };
  const IconMap = {
    processing: Loader2,
    success: CheckCircle2,
    error: XCircle,
  };
  const Icon = IconMap[state.status];

  return (
    <div
      className="app-shell"
      data-testid="guest-payment-method-callback"
      style={{
        backgroundColor: 'var(--app-bg)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 320,
          textAlign: 'center',
          background: 'var(--surface)',
          padding: '32px 24px',
          borderRadius: 22,
          boxShadow: '0 14px 30px rgba(112, 63, 246, 0.14)',
        }}
      >
        <Icon
          size={44}
          color={colorMap[state.status]}
          strokeWidth={1.8}
          className={state.status === 'processing' ? 'animate-spin' : ''}
          data-testid={`guest-payment-method-callback-icon-${state.status}`}
        />
        <div
          data-testid="guest-payment-method-callback-message"
          style={{
            marginTop: 16,
            fontFamily: FONT,
            fontSize: 16,
            color: 'var(--text)',
            lineHeight: 1.5,
          }}
        >
          {state.message}
        </div>
      </div>
    </div>
  );
}
